// -----------------------------------------------------------------------------
// src/Pages/Creator/Start/index.js  – 2025‑06‑30  (Left‑gap + scroll‑lock fix)
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Fade,
  useTheme,
  alpha,
  GlobalStyles,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useNavigate } from "react-router-dom";

import ZapFeed from "./zapfeed/zapfeed";
import PayoutsInvoicing from "./payoutsinvoicing";
import useIsDesktop from "../../../Hooks/useIsDesktop";

/**
 * CreatorStart – ZapFeed‑centric landing page for creators.
 *
 * Problem fixed (Jun 2025):
 *  • A persistent blank strip appeared on the left edge of the viewport after
 *    opening/closing slide‑in components. This was caused by Material UI’s
 *    scroll‑lock behaviour (it injects a `padding‑right` on <body> when a modal
 *    is open). Disabling scroll‑lock and forcing an overflow‑hidden wrapper
 *    removes the jump without compromising UX.
 *  • Minor clean‑ups & tighter centring of ZapFeed.
 */
const CreatorStart = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isDesktop = useIsDesktop();

  /* ------------------------------ Drawer ------------------------------ */
  const [showMoney, setShowMoney] = useState(false);
  const openMoney  = () => setShowMoney(true);
  const closeMoney = () => setShowMoney(false);
  const goInvoice  = () => navigate("/creatorinvoicing");

  /* ---------------------------- ZapFeed box --------------------------- */
  const feedWrapperStyles = {
    mx: "auto",
    maxWidth: 720,
    width: "100%",
    px: { xs: 0, sm: 2 },           // breathing room on tablet/desktop
    animation: {
      xs: "none",
      md: "fadeSlide .6s ease-out",
    },
    "@keyframes fadeSlide": {
      from: { opacity: 0, transform: "translateY(16px)" },
      to:   { opacity: 1, transform: "translateY(0)" },
    },
  };

  return (
    <>
      {/* Removes the scroll‑lock shim Material UI adds when the Drawer is open */}
      <GlobalStyles styles={{ body: { paddingRight: "0px !important" } }} />

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflowX: "hidden",          // ensures no residual horizontal gap
          background: {
            xs: theme.palette.background.default,
            md: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.light,
              0.15
            )} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
          },
        }}
      >
        {/* ------------------------- ZapFeed -------------------------- */}
        <Box sx={feedWrapperStyles}>
          <ZapFeed />
        </Box>

        {/* ---------------------- Payouts Drawer ----------------------- */}
        <Drawer
          anchor={isDesktop ? "right" : "bottom"}
          open={showMoney}
          onClose={closeMoney}
          // Prevent MUI from adding body padding‑right (which caused the gap)
          ModalProps={{
            disableScrollLock: true,
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: "100%",
              height: "100%",
              borderRadius: 0,
              boxShadow: 24,
              overflow: "hidden",
              backdropFilter: "blur(4px)",
            },
          }}
          transitionDuration={280}
        >
          <Box
            sx={{
              position: "relative",
              height: "100%",
              background: theme.palette.background.default,
            }}
          >
            <IconButton
              onClick={closeMoney}
              sx={{ position: "absolute", top: 8, left: 8, zIndex: 10 }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ pt: 7, px: { xs: 1, sm: 3 }, height: "100%", overflow: "auto" }}>
              <PayoutsInvoicing />
            </Box>
          </Box>
        </Drawer>

      </Box>
    </>
  );
};

export default CreatorStart;
